import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { cn } from 'src/utils/cx'
import IcBaselineAutoAwesome from '~icons/ic/baseline-auto-awesome'
import { GALLERY_IMAGES } from '../constants'
import { useNavigateToGenerator } from '../hooks/useNavigateToGenerator'
import css from '../Landing.module.css'
import imgMioHeader from './assets/mio-header.webp'

export function HeroSection() {
  const { t } = useTranslation('blackboard', { keyPrefix: 'welcome' })
  const [prompt, setPrompt] = useState('')
  const navigateToGenerator = useNavigateToGenerator()

  return (
    <>
      <section
        id="hero"
        className="relative flex min-h-screen items-center overflow-hidden pb-16 pt-24"
        style={{
          background:
            'linear-gradient(180deg, var(--landing-pink-bg2) 0%, #fff 100%)',
        }}
      >
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid items-center gap-10 md:grid-cols-2">
            {/* Left: content */}
            <div className="relative z-[2] text-center md:text-left">
              <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-[--landing-pink-light] bg-[--landing-pink-bg] px-4 py-1.5 text-sm font-semibold text-[--landing-pink-dark]">
                <IcBaselineAutoAwesome className="size-4" /> {t('hero.badge')}
              </div>
              <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight text-gray-900 md:text-6xl">
                <Trans
                  i18nKey="blackboard:welcome.hero.heading"
                  components={{
                    highlight: <span className={css.gradientText} />,
                    br: <br />,
                  }}
                />
              </h1>
              <p className="mx-auto mb-8 max-w-[480px] text-lg leading-relaxed text-gray-500 md:mx-0">
                <Trans
                  i18nKey="blackboard:welcome.hero.subtitle"
                  components={{ br: <br /> }}
                />
              </p>

              {/* Prompt input */}
              <form
                className="mx-auto mb-6 flex max-w-[480px] items-center rounded-2xl border-[1.5px] border-gray-200 bg-white p-1.5 pl-5 shadow-sm md:mx-0"
                onSubmit={e => {
                  e.preventDefault()
                  navigateToGenerator(prompt || undefined)
                }}
              >
                <input
                  type="text"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={t('hero.prompt-placeholder')}
                  className="min-w-0 flex-1 bg-transparent text-sm text-gray-600 outline-none placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  className="shrink-0 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-700"
                >
                  {t('hero.generate')}
                </button>
              </form>

              <a
                href="/tsubaki-2/guide"
                className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-[--landing-pink-light] bg-white px-6 py-2.5 text-sm font-semibold text-[--landing-pink] transition-all hover:border-[--landing-pink] hover:bg-[--landing-pink-bg]"
              >
                {t('hero.prompt-guide')}
              </a>
              <p className="mt-4 text-xs text-gray-400">
                {t('hero.no-credit-card')}
              </p>
            </div>

            {/* Right: hero image */}
            <div className="relative z-[1] flex justify-center">
              <div className="relative w-full max-w-[720px]">
                <img
                  src={imgMioHeader}
                  alt={t('hero.image-alt')}
                  className={cn(css.heroCharacter, 'w-full drop-shadow-2xl')}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery scroll strip */}
      <section className="overflow-hidden pb-20 pt-10">
        <div className={cn(css.galleryTrack, 'flex w-max gap-4')}>
          {/* Duplicate images for infinite scroll */}
          {[...GALLERY_IMAGES, ...GALLERY_IMAGES].map((img, i) => (
            <div
              key={i}
              className="h-[300px] w-[220px] shrink-0 overflow-hidden rounded-2xl bg-gray-100 shadow-md transition-transform hover:scale-[1.04] hover:-translate-y-1"
            >
              <img
                src={img.src}
                alt={t(`gallery.images.${i % GALLERY_IMAGES.length}`)}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </section>
    </>
  )
}
